import { createLocalStorageStore } from '@client/helpers/local-storage.helpers';
import { legacyCostDisabledLSKey } from '@client/store/project';
import { useCallback } from 'react';
import {
  DisplayCurrency,
  NO_CURRENCY,
  NoCurrency,
} from './display-currency.model';
import { tryCatch } from '@shared/helpers/utils.helpers';

/** Currencies selectable from the lifecycle scope menu. */
export const DISPLAY_CURRENCY_OPTIONS = ['SEK', 'EUR', 'USD'] as const;
export type SelectableDisplayCurrency =
  (typeof DISPLAY_CURRENCY_OPTIONS)[number];

// defines the order of selection of currencies
const currencyCarousel: Array<DisplayCurrency> = [
  'SEK',
  'USD',
  'EUR',
  NO_CURRENCY,
];

const getNextCurrency = (current: DisplayCurrency): DisplayCurrency => {
  const currentIndex = currencyCarousel.indexOf(current);
  const nextIndex = (currentIndex + 1) % currencyCarousel.length;
  return currencyCarousel[nextIndex] ?? NO_CURRENCY;
};

// fallback value depends on the value of the legacy cost disabled local storage key
const fallbackValue = tryCatch<DisplayCurrency, DisplayCurrency>(() => {
  const lsValue = localStorage.getItem(legacyCostDisabledLSKey);
  return lsValue === 'true' ? NO_CURRENCY : 'SEK';
}, 'SEK').value;

// Keep one store instance during Fast Refresh so already mounted consumers
// and newly loaded controls continue to subscribe to one currency selection.
const hotData = import.meta.hot?.data;
const displayCurrencyStore: ReturnType<
  typeof createLocalStorageStore<DisplayCurrency>
> =
  hotData?.displayCurrencyStore ??
  createLocalStorageStore<DisplayCurrency>(
    'selected_display_currency',
    fallbackValue,
  );

const lastDisplayCurrencyStore: ReturnType<
  typeof createLocalStorageStore<SelectableDisplayCurrency>
> =
  hotData?.lastDisplayCurrencyStore ??
  createLocalStorageStore<SelectableDisplayCurrency>(
    'last_selected_display_currency',
    'SEK',
  );

if (hotData) {
  hotData.displayCurrencyStore = displayCurrencyStore;
  hotData.lastDisplayCurrencyStore = lastDisplayCurrencyStore;
}

type CurrencyDisplayState =
  | {
      current: Exclude<DisplayCurrency, NoCurrency>;
      setNext: () => void;
      isDisabled: false;
      isEnabled: true;
    }
  | {
      current: NoCurrency;
      setNext: () => void;
      isDisabled: true;
      isEnabled: false;
    };

export const useDisplayCurrency = (): CurrencyDisplayState => {
  const current = displayCurrencyStore.useStore();
  const setNext = useCallback(
    () => displayCurrencyStore.set(getNextCurrency(current)),
    [current],
  );
  if (current === NO_CURRENCY) {
    return { current, setNext, isDisabled: true, isEnabled: false };
  }
  return { current, setNext, isDisabled: false, isEnabled: true };
};

/** Default currency restored when cost is re-enabled. */
const DEFAULT_DISPLAY_CURRENCY: Exclude<DisplayCurrency, NoCurrency> = 'SEK';

const selectableDisplayCurrencySet = new Set<string>(DISPLAY_CURRENCY_OPTIONS);

const isSelectableDisplayCurrency = (
  currency: DisplayCurrency,
): currency is SelectableDisplayCurrency =>
  currency !== NO_CURRENCY && selectableDisplayCurrencySet.has(currency);

/**
 * Set the active display currency and show cost. Persists the choice for when cost is re-enabled.
 */
export const setDisplayCurrency = (
  currency: SelectableDisplayCurrency,
): void => {
  lastDisplayCurrencyStore.set(currency);
  displayCurrencyStore.set(currency);
};

/**
 * Show or hide cost entirely. Hiding clears the currency selection ({@link NO_CURRENCY});
 * showing restores the last selected currency. Keeps the Cost checkbox and currency picks in sync.
 */
export const setCostVisible = (visible: boolean): void => {
  if (visible) {
    displayCurrencyStore.set(
      lastDisplayCurrencyStore.get() ?? DEFAULT_DISPLAY_CURRENCY,
    );
    return;
  }

  const current = displayCurrencyStore.get() ?? NO_CURRENCY;
  if (isSelectableDisplayCurrency(current)) {
    lastDisplayCurrencyStore.set(current);
  }
  displayCurrencyStore.set(NO_CURRENCY);
};
